import React, { useState, useEffect } from 'react';
import ProblemCards from './ProblemCards';
import {
  registerUserForContestAuth,
  checkUserRegistration,
  checkSubmission
} from '../api/contestApi';
import { useAuthUser } from './useAuthUser';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Contests = () =>
{
  const { userAuth } = useAuthUser();
  const navigate = useNavigate();

  const [countdowns, setCountdowns] = useState({});
  const [currentView, setCurrentView] = useState('contests');
  const [selectedContestId, setSelectedContestId] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState({});
  const [endingTime, setEndingTime] = useState(0);
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);

  /* ---------------- helpers ---------------- */

  const isContestEnded = (date, time, length) =>
  {
    const [d, m, y] = date.split('-');
    const [h, min] = time.split(':');
    const start = new Date(y, m - 1, d, h, min);
    const end = new Date(start.getTime() + length * 1000);
    return new Date() >= end;
  };

  const calculateTimeLeft = (date, time, length) =>
  {
    const [d, m, y] = date.split('-');
    const [h, min] = time.split(':');
    const start = new Date(y, m - 1, d, h, min);
    const now = new Date();

    if (now < start)
    {
      const diff = start - now;
      const days = Math.floor((diff/(1000 * 60 * 1440)));
      const hours = Math.floor(((diff)/(1000*60*60))%24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      if (days>0)
      {
        return `${days}d ${hours}h`;
      }
      if (hours>0)
      {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m ${seconds}s`;
    }

    if (isContestEnded(date, time, length))
    {
      return 'Contest Ended';
    }

    return 'Contest Started!';
  };

  const renderAction = (card, status) => (
    <button
      className={`sm:w-auto px-3 py-1 rounded text-white text-sm sm:text-lg ${
        status === 'Contest Started!'
          ? 'bg-green-600'
          : status === 'Contest Ended'
            ? 'bg-red-600'
            : registrationStatus[card.id]
              ? 'bg-gray-500'
              : 'bg-blue-600'
      }`}
      disabled = {
        status === 'Contest Ended' ||
        (status === 'Contest Started!' && !registrationStatus[card.id]) ||
        (status !== 'Contest Ended' &&
         status !== 'Contest Started!' &&
         registrationStatus[card.id])
      }
      onClick={() =>
        status === 'Contest Started!'
          ? handleContestEntry(card.id, card.date, card.time, card.length)
          : handleRegistration(card.id)
      }
    >
      {status === 'Contest Started!'
        ? 'Enter'
        : registrationStatus[card.id]
          ? 'Registered'
          : 'Register'}
    </button>
  );

  /* ---------------- effects ---------------- */

  useEffect(() => {
    const FetchCardData = async () => {
      try
      {
        const response = await axios.get("https://eduendurance-backend.onrender.com/api/contests/fetch-contests-card-data");
        if (response.data?.Data)
        {
          setCardData(response.data.Data);
        }
      } catch (err)
      {
        console.error("Error fetching card data:", err);
      } finally
      {
        setLoading(false);
      }
    };

    FetchCardData();
  }, []);   // empty dependency array

  useEffect(() =>
  {
    if (!userAuth) return;
    if (loading) return;
    const load = async () =>
    {
      const s = {};
      for (const c of cardData)
      {
        s[c.id] = await checkUserRegistration(c.id, userAuth);
      }
      setRegistrationStatus(s);
    };
    load();
  }, [userAuth, cardData]);

  useEffect(() =>
  {
    const update = () =>
    {
      if (loading) return;
      const c = {};
      cardData.forEach(card =>
      {
        c[card.id] = calculateTimeLeft(card.date, card.time, card.length);
      });
      setCountdowns(c);
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [cardData]);

  /* ---------------- handlers ---------------- */

  const handleRegistration = async (id) =>
  {
    if (!userAuth)
    {
      alert("Login required");
      return;
    }
    const res = await registerUserForContestAuth(id, userAuth);
    if (res.success)
    {
      setRegistrationStatus({ ...registrationStatus, [id]: true });
    }
  };

  const handleContestEntry = async (id, date, time, length) =>
  {
    const attempted = await checkSubmission(id, userAuth);
    if (attempted)
    {
      alert("You have already submitted your attempt for this contest.");
      return;
    }

    const [d, m, y] = date.split('-');
    const [h, min] = time.split(':');
    const start = new Date(y, m - 1, d, h, min);
    const end = new Date(start.getTime() + length * 1000);
    const seconds = Math.floor((end - new Date()) / 1000);

    setSelectedContestId(id);
    setEndingTime(seconds);
    setCurrentView('problems');
  };

  if (currentView === 'problems')
  {
    return (
      <div>
        <button
          onClick={() => setCurrentView('contests')}
          className="mb-4 bg-gray-500 text-white px-4 py-2 rounded"
        >
          ← Back
        </button>
        <ProblemCards contestId={selectedContestId} timeLeft={endingTime} />
      </div>
    );
  }

  if (loading)
  {
    return (
      <div className="flex items-center justify-center h-screen text-xl sm:text-3xl">
        Importing The Contests Data... 
      </div>
    );
  }

  return (
    <>
      {/* ================= DESKTOP TABLE ================= */}
      <div className="hidden sm:block overflow-x-auto border-2 border-gray-700 rounded-lg">
        <table className="w-full border-collapse text-lg">
          <thead className="bg-gray-200 border-b-2 border-gray-700">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Writers</th>
              <th className="p-3">Start</th>
              <th className="p-3">Length</th>
              <th className="p-3">Status</th>
              <th className="p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {cardData.map(card =>
            {
              const status = countdowns[card.id];
              return (
                <tr key={card.id} className="hover:bg-gray-100 text-center">
                  <td className="p-3 font-semibold border-b-2 border-gray-600">
                    {card.header}
                  </td>
                  <td className="p-3 border-b-2 border-gray-600">
                    {card.writers}
                  </td>
                  <td className="p-3 border-b-2 border-gray-600">
                    {card.date} {card.time}
                  </td>
                  <td className="p-3 border-b-2 border-gray-600">
                    {card.displayLength}
                  </td>
                  <td className="p-3 border-b-2 border-gray-600">
                    {status}
                  </td>
                  <td className="p-3 border-b-2 border-gray-600">
                    {renderAction(card, status)}
                    {status === 'Contest Ended' && (
                      <button
                        className="block mx-auto mt-2 px-3 py-1 text-lg bg-blue-600 text-white rounded hover:bg-blue-700"
                        onClick={() =>
                          navigate(`/contest_leaderboard/${card.id}/1`)
                        }
                      >
                        View Leaderboard
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ================= MOBILE CARDS ================= */}
      <div className="sm:hidden space-y-2 mt-2 mb-1 px-1">
        {cardData.map(card =>
        {
          const status = countdowns[card.id];
          return (
            <div
              key={card.id}
              className="bg-white text-black rounded-lg shadow p-4 border border-gray-300"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{card.header}</h3>
                  <p className="text-sm text-gray-600">By {card.writers}</p>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {renderAction(card, status)}

                  {status === 'Contest Ended' && (
                    <button
                      onClick={() =>
                        navigate(`/contest_leaderboard/${card.id}/1`)
                      }
                      className="text-blue-600 text-sm hover:underline"
                    >
                      View Leaderboard
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-3 text-sm space-y-1">
                <p><b>Start:</b> {card.date} {card.time}</p>
                <p><b>Length:</b> {card.displayLength}</p>
                <p><b>Status:</b> {status}</p>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default Contests;
